import { Box, FaceLandmarks68 } from '@vladmandic/face-api';

import { SelfieStep as SelfiePose } from 'verify/lib/interfaces/selfie';

export interface PoseState {
  currentPose: SelfiePose | 'done';
  hasFace: boolean;
  invalidReasons?: InvalidSelfieReason[] | null;
  poseIndex: number;
}

export interface PoseStateManagerConfigs {
  poseOrder: SelfiePose[];
  requireHigherAccuracyCapture: boolean;
  onCapture: (pose: SelfiePose, canvas: HTMLCanvasElement, isManual: boolean) => void;
}

export type PoseDataAttributes = {
  elapsedTime: number;
  faceBox: Box | undefined;
  faceCount: number;
  faceDimensions: { width: number; height: number };
  horizontalAngle: number;
  landmarks?: FaceLandmarks68 | null;
  videoDimensions: { width: number; height: number };
};

/**
 * A class for managing what pose the user should be attempting
 * Should be called on each tick of a video stream
 */
export class PoseStateManager {
  private readonly configs: PoseStateManagerConfigs;
  private currentState: PoseState;

  constructor(configs: PoseStateManagerConfigs) {
    this.configs = configs;

    // Initialize with first state
    this.currentState = {
      currentPose: configs.poseOrder[0],
      hasFace: false,
      invalidReasons: null,
      poseIndex: 0,
    };
  }

  public getNextPoseState({
    canvas,
    forceCapture,
    poseData,
  }: {
    canvas: HTMLCanvasElement;
    forceCapture: boolean;
    poseData: PoseDataAttributes | null;
  }): PoseState {
    let { currentPose, poseIndex } = this.currentState;

    if (currentPose === 'done' || (poseData == null && !forceCapture)) {
      return this.currentState;
    }

    let invalidReasons;

    if (poseData) {
      invalidReasons = this.getInvalidReasons(poseData, currentPose);
    }

    if (
      forceCapture ||
      (!invalidReasons && poseData != null && this.canCapture(poseData, currentPose))
    ) {
      const { poseOrder, onCapture } = this.configs;

      onCapture(currentPose, canvas, forceCapture);

      poseIndex = poseIndex + 1;
      invalidReasons = null;
      currentPose = poseIndex > poseOrder.length - 1 ? 'done' : poseOrder[poseIndex];
    }

    return (this.currentState = {
      currentPose,
      hasFace: forceCapture ? true : poseData != null && poseData.faceCount >= 1,
      invalidReasons,
      poseIndex,
    });
  }

  private getInvalidReasons(
    data: PoseDataAttributes,
    currentPose: SelfiePose,
  ): InvalidSelfieReason[] | null {
    const reasons = [];
    for (const reason in INVALID_REASON_TRIGGERS) {
      const selfieReason = reason as InvalidSelfieReason;
      if (
        INVALID_REASON_TRIGGERS[selfieReason].isEnabled(data) &&
        INVALID_REASON_TRIGGERS[selfieReason].isTriggered(
          data,
          currentPose,
          this.configs.requireHigherAccuracyCapture,
        )
      ) {
        reasons.push(selfieReason);
      }
    }

    return reasons.length > 0 ? reasons : null;
  }

  private canCapture(data: PoseDataAttributes, currentPose: SelfiePose) {
    // If the elapsedTime is less than the delay config, than use sticter trigger
    return POSE_CAPTURE_TRIGGERS[currentPose](data);
  }
}

// The angle that the face must pass to capture a screenshot.
export const POSE_CENTER_THRESHOLD = 5;
export const POSE_SIDE_THRESHOLD = 20;

type CaptureFunc = (data: PoseDataAttributes) => boolean;

const hasOneFace = (elapsedTime: number, faceCount: number) => {
  return elapsedTime > MULTIPLE_FACES_BLOCKING_TIMEOUT ? faceCount >= 1 : faceCount === 1;
};

const POSE_CAPTURE_TRIGGERS: Record<SelfiePose, CaptureFunc> = {
  [SelfiePose.Left]: (data) => {
    return (
      hasOneFace(data.elapsedTime, data.faceCount) && data.horizontalAngle < -POSE_SIDE_THRESHOLD
    );
  },
  [SelfiePose.Right]: (data) => {
    return (
      hasOneFace(data.elapsedTime, data.faceCount) && data.horizontalAngle > POSE_SIDE_THRESHOLD
    );
  },
  [SelfiePose.Center]: (data) => {
    return (
      hasOneFace(data.elapsedTime, data.faceCount) &&
      -POSE_CENTER_THRESHOLD < data.horizontalAngle &&
      data.horizontalAngle < POSE_CENTER_THRESHOLD
    );
  },
};

/**
 * Only block on the multiple faces check for this long.
 * This check can be flaky on mobile Safari.
 */
const MULTIPLE_FACES_BLOCKING_TIMEOUT = 10000;

export enum InvalidSelfieReason {
  MultipleFaces = 'multiple_faces',
  TooFar = 'too_far',
  TooClose = 'too_close',
  NotCentered = 'not_centered',
  NoFace = 'no_face',
}

type InvalidReasonConfigs = {
  isTriggered: (
    data: PoseDataAttributes,
    currentPose: SelfiePose,
    requireHigherAccuracyCapture: boolean,
  ) => boolean;
  isEnabled: (data: PoseDataAttributes) => boolean;
};

const MIN_FACE_RATIO = 0.35;
const MIN_FACE_RATIO_HIGHER_ACCURACY = 0.45;
const MAX_FACE_RATIO = 0.8;
const MAX_LEFT_RIGHT_OFFSET_RATIO = 3;

/**
 *  The order of these reasons matters -- it will check each trigger sequentially
 *  Place the reasons in the order that you'd want them to appear to the user
 */
const INVALID_REASON_TRIGGERS: Record<InvalidSelfieReason, InvalidReasonConfigs> = {
  [InvalidSelfieReason.MultipleFaces]: {
    isTriggered: (data: PoseDataAttributes) => {
      return data.faceCount > 1;
    },
    isEnabled: (data) => {
      return data.elapsedTime <= MULTIPLE_FACES_BLOCKING_TIMEOUT;
    },
  },
  [InvalidSelfieReason.TooFar]: {
    isTriggered: (
      data: PoseDataAttributes,
      currentPose: SelfiePose,
      requireHigherAccuracyCapture = false,
    ) => {
      const maxFaceDimension = Math.max(data.faceDimensions.width, data.faceDimensions.width);
      const minVideoDimension = Math.min(data.videoDimensions.width, data.videoDimensions.height);

      const minFaceRatio =
        requireHigherAccuracyCapture && currentPose === SelfiePose.Center
          ? MIN_FACE_RATIO_HIGHER_ACCURACY
          : MIN_FACE_RATIO;

      return data.faceCount > 0 && maxFaceDimension < minVideoDimension * minFaceRatio;
    },
    isEnabled: () => {
      return true;
    },
  },
  [InvalidSelfieReason.TooClose]: {
    isTriggered: (data: PoseDataAttributes) => {
      const maxFaceDimension = Math.max(data.faceDimensions.width, data.faceDimensions.width);
      const minVideoDimension = Math.min(data.videoDimensions.width, data.videoDimensions.height);

      return data.faceCount > 0 && maxFaceDimension > minVideoDimension * MAX_FACE_RATIO;
    },
    isEnabled: () => {
      return true;
    },
  },
  [InvalidSelfieReason.NotCentered]: {
    isTriggered: (
      data: PoseDataAttributes,
      currentPose: SelfiePose,
      requireHigherAccuracyCapture = false,
    ) => {
      const { faceBox, videoDimensions, faceCount, landmarks } = data;
      if (!requireHigherAccuracyCapture || faceBox == null || faceCount === 0) {
        return false;
      }

      // If the face extends beyond the top or bottom of the frame, require the user to center themselves
      if (faceBox.top < 0 || faceBox.bottom > videoDimensions.height) {
        return true;
      }

      // Make sure the user's nose is within the middle 20% of the frame when facing forward
      if (
        landmarks != null &&
        landmarks?.getNose().length > 2 &&
        currentPose === SelfiePose.Center
      ) {
        // Middle bottom nose point. See `horizontalAngleFromLandmarks` for more details
        const nosePoint = landmarks.getNose()[3];

        // Landmarks are relative to facebox position -- convert them to be relative to entire canvas
        const noseX = nosePoint.x + faceBox.x;
        const noseY = nosePoint.y + faceBox.y;

        const noseBoundSquareSize = Math.min(videoDimensions.width, videoDimensions.height) * 0.4;

        const noseLeftBound = videoDimensions.width / 2 - noseBoundSquareSize / 2;
        const noseRightBound = videoDimensions.width / 2 + noseBoundSquareSize / 2;
        const noseTopBound = videoDimensions.height / 2 - noseBoundSquareSize / 2;
        const noseBottomBound = videoDimensions.height / 2 + noseBoundSquareSize / 2;

        if (
          noseX < noseLeftBound ||
          noseX > noseRightBound ||
          noseY < noseTopBound ||
          noseY > noseBottomBound
        ) {
          return true;
        }
      }

      // Sometimes the side of the face can be beyond the video borders.
      // Setting a minimum of 1 ensures we never divide by zero, and allows us to rely
      // on the offset check below to detect when left and right sides are out of bounds
      const rightMargin = Math.max(videoDimensions.width - faceBox.right, 1);
      const leftMargin = Math.max(faceBox.left, 1);

      // Always take the larger distance / smaller distance, regardless of which side is larger
      // (i.e. the face is 3x closer to the right than the left, or 2x closer to the left than the right)
      const leftRightOffsetRatio =
        Math.max(leftMargin, rightMargin) / Math.min(leftMargin, rightMargin);

      return leftRightOffsetRatio > MAX_LEFT_RIGHT_OFFSET_RATIO;
    },
    isEnabled: () => {
      return true;
    },
  },
  [InvalidSelfieReason.NoFace]: {
    isTriggered: (data: PoseDataAttributes) => {
      return data.faceCount <= 0;
    },
    isEnabled: () => {
      return true;
    },
  },
};
